import { Gender } from '@starjack/utils';
import {
    TicketHolder,
    OrderTicketCollection,
    createOrderTicketCollectionId,
} from '../model/orderTicket.types';
import { v4 as uuid } from 'uuid';

export interface ValidatedImportRow {
    ticketName: string;
    productId: string;
    personName: string;
    title: string;
    firstname: string;
    lastname: string;
    birthdate: Date | string;
}

interface ProcessDataImportParams {
    importData: ValidatedImportRow[];
    availableTicketCollections: OrderTicketCollection[];
    currentDate: Date;
}

export const processDataImport = ({
    importData,
    availableTicketCollections,
    currentDate,
}: ProcessDataImportParams): OrderTicketCollection[] => {
    const collectionMap = new Map<string, OrderTicketCollection>();

    availableTicketCollections.forEach((collection) => {
        collectionMap.set(collection.id, collection);
    });

    const collectionUpdates = new Map<
        string,
        {
            collection: OrderTicketCollection;
            newTicketHolders: Map<string, TicketHolder[]>;
        }
    >();

    importData.forEach((row) => {
        const collectionId = createOrderTicketCollectionId(
            row.ticketName,
            currentDate
        );
        const collection = collectionMap.get(collectionId);

        if (!collection) {
            throw new Error(`Ticket name ${row.ticketName} not found`);
        }

        if (!collectionUpdates.has(collectionId)) {
            collectionUpdates.set(collectionId, {
                collection: { ...collection },
                newTicketHolders: new Map(),
            });
        }

        const update = collectionUpdates.get(collectionId)!;

        if (!update.newTicketHolders.has(row.productId)) {
            update.newTicketHolders.set(row.productId, []);
        }

        const ticketHolder: TicketHolder = {
            id: uuid(),
            gender: row.title as Gender,
            firstname: row.firstname,
            lastname: row.lastname,
            birthDay:
                typeof row.birthdate === 'string'
                    ? new Date(row.birthdate)
                    : row.birthdate,
            ticketId: row.productId,
        };

        update.newTicketHolders.get(row.productId)!.push(ticketHolder);
    });

    const updatedCollections: OrderTicketCollection[] = [];

    collectionUpdates.forEach(({ collection, newTicketHolders }) => {
        let updatedCollection = { ...collection };

        newTicketHolders.forEach((ticketHolders, productId) => {
            if (!ticketHolders.length) return;

            const existingHolders =
                updatedCollection.ticketHoldersMap[productId] || [];
            updatedCollection = {
                ...updatedCollection,
                ticketHoldersMap: {
                    ...updatedCollection.ticketHoldersMap,
                    [productId]: [...existingHolders, ...ticketHolders],
                },
                numberOfTicketHolders:
                    updatedCollection.numberOfTicketHolders +
                    ticketHolders.length,
            };
        });

        updatedCollections.push(updatedCollection);
    });

    return updatedCollections;
};
